import { useLayoutEffect, useRef, useState } from 'react';

import { FIGURE } from '@/components/core/markdown/diagramTheme';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import { stripMath } from '@/lib/markdown/previewText';
import { cn } from '@/lib/utils';
import type { LabelView, VisualLabel } from '@/types/visual';

// A picture with labels the APP draws. Illustrations carry no text (image
// models garble lettering), so the labels live in data - which is what lets
// one picture serve every view:
//   labels    - every label, in the side margins with leader lines (teach)
//   hidden    - one label replaced by "?"                     (recall)
//   numbered  - numbered markers, no names                     (test)
//   highlight - only one part named and emphasised             (summarise)
// Below WIDE px there are no side margins: numbered markers + a key below.

// Side labels are sized to fit: about this many px per character of the
// 13 px label, plus its padding and the gap to the picture. Narrower than the
// longest label needs, the picture takes numbered markers and a key instead.
const CHAR_PX = 7.4;
const LABEL_PAD = 14;
const LEADER = 16;
const MIN_SIDE_PICTURE = 180;
const GAP = 26; // minimum vertical distance between two labels, px
const MARKER = 6;
const NUMBER_R = 11; // a numbered marker's radius, px

interface Placed extends VisualLabel {
  n: number;
  side: 'left' | 'right';
  px: number; // marker, px in the box
  py: number;
  ly: number; // label row, px
}

function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width)
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

/** Labels split left/right of the picture by where they point, each side
 *  spread down so no two rows collide, kept inside the box. */
function place(labels: VisualLabel[], left: number, size: number): Placed[] {
  const all = labels.map((l, i) => ({
    ...l,
    n: i + 1,
    side: (l.x < 50 ? 'left' : 'right') as Placed['side'],
    px: left + (l.x / 100) * size,
    py: (l.y / 100) * size,
    ly: 0,
  }));
  for (const side of ['left', 'right'] as const) {
    const rows = all.filter(p => p.side === side).sort((a, b) => a.py - b.py);
    let y = GAP / 2;
    for (const r of rows) {
      r.ly = Math.max(r.py, y);
      y = r.ly + GAP;
    }
    const overflow = y - GAP - (size - GAP / 2);
    if (overflow > 0) rows.forEach(r => (r.ly -= overflow));
  }
  return all;
}

/** Markers closer than `gap` px (two parts side by side, a stoma and its
 *  guard cells) are pushed apart along the line between them, so neither
 *  hides the other; each stays on the picture. */
export function spread<T extends { px: number; py: number }>(
  markers: T[],
  gap: number,
  bounds: { left: number; size: number }
): T[] {
  const out = markers.map(m => ({ ...m }));
  const lo = bounds.left + gap / 2;
  const hi = bounds.left + bounds.size - gap / 2;
  for (let round = 0; round < 8; round++) {
    let moved = false;
    for (let i = 0; i < out.length; i++)
      for (let j = i + 1; j < out.length; j++) {
        const a = out[i],
          b = out[j];
        const dx = b.px - a.px,
          dy = b.py - a.py;
        const d = Math.hypot(dx, dy);
        if (d >= gap) continue;
        // Same point: push apart sideways.
        const [ux, uy] = d > 0.01 ? [dx / d, dy / d] : [1, 0];
        const push = (gap - d) / 2;
        a.px -= ux * push;
        a.py -= uy * push;
        b.px += ux * push;
        b.py += uy * push;
        moved = true;
      }
    for (const m of out) {
      m.px = Math.min(hi, Math.max(lo, m.px));
      m.py = Math.min(bounds.size - gap / 2, Math.max(gap / 2, m.py));
    }
    if (!moved) break;
  }
  return out;
}

/** Whether the labels fit beside the picture, and the picture's size. */
export function sideLayout(
  width: number,
  maxSize: number,
  labels: VisualLabel[],
  view: LabelView
): { wide: boolean; size: number } {
  const margin = Math.max(
    0,
    ...labels
      .filter(l => shown(view, l.id) !== 'none')
      .map(l => {
        const text = shown(view, l.id) === 'hidden' ? '?' : l.text;
        return Math.ceil(text.length * CHAR_PX) + LABEL_PAD + LEADER;
      })
  );
  const side = Math.min(maxSize, Math.round(width * 0.6), width - 2 * margin);
  const wide =
    view.mode !== 'numbered' && side >= Math.min(maxSize, MIN_SIDE_PICTURE);
  return { wide, size: wide ? side : Math.min(width, maxSize) };
}

function shown(view: LabelView, id: string): 'name' | 'hidden' | 'none' {
  switch (view.mode) {
    case 'labels':
      return 'name';
    case 'hidden':
      return id === view.target ? 'hidden' : 'name';
    case 'highlight':
      return id === view.target ? 'name' : 'none';
    case 'numbered':
      return 'none';
  }
}

export function labelledAria(
  alt: string,
  labels: VisualLabel[],
  view: LabelView
): string {
  // A screen-reader name is plain text: maths as words.
  return stripMath(ariaText(alt, labels, view));
}

function ariaText(alt: string, labels: VisualLabel[], view: LabelView): string {
  switch (view.mode) {
    case 'numbered':
      return `${alt}. Parts numbered 1 to ${labels.length}.`;
    case 'hidden': {
      const rest = labels.filter(l => l.id !== view.target).map(l => l.text);
      return `${alt}. Labelled: ${rest.join(', ')}; one label hidden.`;
    }
    case 'highlight': {
      const t = labels.find(l => l.id === view.target);
      return `${alt}. Highlighted: ${t?.text ?? ''}.`;
    }
    default:
      return `${alt}. Labelled: ${labels.map(l => l.text).join(', ')}.`;
  }
}

function Marker({
  p,
  numbered,
  strong,
  asked = false,
}: {
  p: Placed;
  numbered: boolean;
  strong: boolean;
  /** The part a recall card asks for: "?" in place of its number. */
  asked?: boolean;
}) {
  if (numbered) {
    return (
      <g>
        <circle
          cx={p.px}
          cy={p.py}
          r={NUMBER_R}
          fill={FIGURE.series[0]}
          stroke='#fff'
          strokeWidth={2}
        />
        <text
          x={p.px}
          y={p.py + 4}
          textAnchor='middle'
          fontFamily={FIGURE.font}
          fontSize={12}
          fontWeight={700}
          fill='#fff'
        >
          {asked ? '?' : p.n}
        </text>
      </g>
    );
  }
  return (
    <circle
      cx={p.px}
      cy={p.py}
      r={strong ? MARKER + 2 : MARKER}
      fill={strong ? FIGURE.series[0] : FIGURE.series[1]}
      stroke='#fff'
      strokeWidth={2}
    />
  );
}

function SideLabels({
  placed,
  view,
  width,
  left,
  size,
}: {
  placed: Placed[];
  view: LabelView;
  width: number;
  left: number;
  size: number;
}) {
  const visible = placed.filter(p => shown(view, p.id) !== 'none');
  return (
    <>
      <svg
        className='pointer-events-none absolute inset-0'
        width={width}
        height={size}
        aria-hidden='true'
      >
        {visible.map(p => {
          const lx = p.side === 'left' ? left - 8 : left + size + 8;
          return (
            <polyline
              key={p.id}
              points={`${p.px},${p.py} ${lx},${p.ly} ${p.side === 'left' ? lx - 6 : lx + 6},${p.ly}`}
              fill='none'
              stroke={FIGURE.muted}
              strokeWidth={1}
            />
          );
        })}
        {placed.map(p => (
          <Marker
            key={p.id}
            p={p}
            numbered={view.mode === 'numbered'}
            strong={shown(view, p.id) !== 'name' || view.mode === 'highlight'}
          />
        ))}
      </svg>
      {visible.map(p => {
        const hidden = shown(view, p.id) === 'hidden';
        return (
          <span
            key={p.id}
            aria-hidden='true'
            className={cn(
              'absolute -translate-y-1/2 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[13px] font-medium leading-tight',
              hidden &&
                'border border-dashed px-2.5 font-semibold tracking-wide'
            )}
            style={{
              top: p.ly,
              color: hidden ? FIGURE.series[0] : FIGURE.ink,
              borderColor: hidden ? FIGURE.series[2] : undefined,
              background: hidden ? FIGURE.brandSoft : undefined,
              ...(p.side === 'left'
                ? { right: width - left + 16 }
                : { left: left + size + 16 }),
            }}
          >
            {hidden ? '?' : <InlineMarkdown>{p.text}</InlineMarkdown>}
          </span>
        );
      })}
    </>
  );
}

function NumberedKey({ placed, view }: { placed: Placed[]; view: LabelView }) {
  if (view.mode === 'numbered') return null;
  // Summarise names one part; the key says only that one.
  // Recall asks for one part: its marker shows "?", so the key leaves it out.
  const rows =
    view.mode === 'highlight'
      ? placed.filter(p => p.id === view.target)
      : view.mode === 'hidden'
        ? placed.filter(p => p.id !== view.target)
        : placed;
  return (
    <ol
      aria-hidden='true'
      className='mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-left text-[13px] sm:grid-cols-2'
      style={{ color: FIGURE.ink }}
    >
      {rows.map(p => {
        const state = shown(view, p.id);
        return (
          <li key={p.id} className='flex items-baseline gap-2'>
            <span
              className='font-mono text-xs font-semibold'
              style={{ color: FIGURE.series[0] }}
            >
              {p.n}
            </span>
            <span className={cn(state === 'none' && 'opacity-40')}>
              {state === 'none' ? (
                '…'
              ) : (
                <InlineMarkdown>{p.text}</InlineMarkdown>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** Narrow layout: numbered markers on the picture, names in the key below. */
function NumberedMarkers({
  placed,
  view,
  width,
  size,
}: {
  placed: Placed[];
  view: LabelView;
  width: number;
  size: number;
}) {
  return (
    <svg
      className='pointer-events-none absolute inset-0'
      width={width}
      height={size}
      aria-hidden='true'
    >
      {placed.map(p => {
        // Summarise names one part: only it is numbered (its key row); the
        // others stay plain dots.
        const target = view.mode === 'highlight' && p.id === view.target;
        return (
          <Marker
            key={p.id}
            p={p}
            numbered={view.mode !== 'highlight' || target}
            strong={target}
            asked={view.mode === 'hidden' && p.id === view.target}
          />
        );
      })}
    </svg>
  );
}

export function LabelledDiagram({
  src,
  alt,
  labels,
  view = { mode: 'labels' },
  maxSize = 420,
  onError,
  className,
}: {
  src: string;
  alt: string;
  labels: VisualLabel[];
  view?: LabelView;
  /** Largest side of the picture, px - cards pass a smaller one. */
  maxSize?: number;
  /** The picture did not load (the caller drops the figure). */
  onError?: () => void;
  className?: string;
}) {
  const { ref, width } = useWidth();
  const { wide, size } = sideLayout(width, maxSize, labels, view);
  const left = Math.round((width - size) / 2);
  // Numbered markers are wider than the side layout's dots.
  const placed = spread(
    place(labels, left, size),
    wide ? 2 * MARKER + 4 : 2 * NUMBER_R + 2,
    { left, size }
  );
  return (
    <div
      ref={ref}
      role='img'
      aria-label={labelledAria(alt, labels, view)}
      className={cn('w-full', className)}
      data-labelled-diagram
      data-view={view.mode}
    >
      <div className='relative' style={{ height: size || undefined }}>
        <img
          src={src}
          alt=''
          onError={onError}
          className='absolute top-0 rounded-md'
          style={{ left, width: size, height: size }}
        />
        {width > 0 &&
          (wide ? (
            <SideLabels
              placed={placed}
              view={view}
              width={width}
              left={left}
              size={size}
            />
          ) : (
            <NumberedMarkers
              placed={placed}
              view={view}
              width={width}
              size={size}
            />
          ))}
      </div>
      {width > 0 && !wide && <NumberedKey placed={placed} view={view} />}
    </div>
  );
}
